import {
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type {
  Chapter,
  Source,
  Title,
} from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

interface ChapterRow extends Chapter {
  sources: Source
}

interface NewChapter {
  source_id: string
  chapter_no: string
  title: string
  url: string
}

const EMPTY_NEW: NewChapter = {
  source_id: '',
  chapter_no: '',
  title: '',
  url: '',
}

export default function ChaptersPage() {
  const { id: titleId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [titleName, setTitleName] = useState('')
  const [sources, setSources] = useState<Source[]>([])
  const [chapters, setChapters] = useState<ChapterRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [newChapter, setNewChapter] = useState<NewChapter>(EMPTY_NEW)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!titleId) return
    const resolvedTitleId = titleId
    async function load() {
      const [titleRes, sourcesRes, chaptersRes] = await Promise.all([
        supabase
          .from('titles')
          .select('canonical_name')
          .eq('id', resolvedTitleId)
          .single(),
        supabase.from('sources').select('*').order('name'),
        supabase
          .from('chapters')
          .select('*, sources(*)')
          .eq('title_id', resolvedTitleId)
          .order('chapter_no', { ascending: false }),
      ])
      setTitleName((titleRes.data as Title | null)?.canonical_name ?? '')
      setSources(sourcesRes.data ?? [])
      setChapters((chaptersRes.data ?? []) as ChapterRow[])
      if (sourcesRes.data?.length) {
        setNewChapter((prev) => ({
          ...prev,
          source_id: sourcesRes.data[0].id,
        }))
      }
      setIsLoading(false)
    }
    load()
  }, [titleId])

  async function handleAddChapter() {
    setError(null)
    const chNo = Number(newChapter.chapter_no)
    if (
      !newChapter.source_id ||
      !newChapter.chapter_no ||
      Number.isNaN(chNo) ||
      !newChapter.url.trim()
    ) {
      setError('Source, chapter number, and URL are required.')
      return
    }
    if (!titleId) return
    setSaving(true)
    const { data, error: err } = await supabase
      .from('chapters')
      .insert({
        title_id: titleId,
        source_id: newChapter.source_id,
        chapter_no: chNo,
        title: newChapter.title.trim() || null,
        url: newChapter.url.trim(),
      })
      .select('*, sources(*)')
      .single()

    if (err) {
      setError(err.message)
      setSaving(false)
      return
    }
    setChapters((prev) => [data as ChapterRow, ...prev])
    setNewChapter({ ...EMPTY_NEW, source_id: newChapter.source_id })
    setIsAdding(false)
    setSaving(false)
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this chapter?')) return
    await supabase.from('chapters').delete().eq('id', id)
    setChapters((prev) => prev.filter((c) => c.id !== id))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-7 w-7 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/admin/manga')}
        className="mb-4 inline-flex items-center gap-1 text-gray-500 text-sm hover:text-gray-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to titles
      </button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-bold text-gray-900 text-xl">Chapters</h1>
          <p className="text-gray-500 text-sm">{titleName}</p>
        </div>
        {!isAdding && (
          <button
            type="button"
            onClick={() => {
              setIsAdding(true)
              setError(null)
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add chapter
          </button>
        )}
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-red-600 text-sm">
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Ch #
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Title
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Source
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                URL
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isAdding && (
              <tr className="bg-blue-50">
                <td className="px-4 py-2">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={newChapter.chapter_no}
                    onChange={(e) =>
                      setNewChapter((p) => ({
                        ...p,
                        chapter_no: e.target.value,
                      }))
                    }
                    placeholder="1"
                    className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={newChapter.title}
                    onChange={(e) =>
                      setNewChapter((p) => ({ ...p, title: e.target.value }))
                    }
                    placeholder="Optional title"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={newChapter.source_id}
                    onChange={(e) =>
                      setNewChapter((p) => ({
                        ...p,
                        source_id: e.target.value,
                      }))
                    }
                    className="rounded border border-gray-300 px-2 py-1 text-sm"
                  >
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="url"
                    value={newChapter.url}
                    onChange={(e) =>
                      setNewChapter((p) => ({ ...p, url: e.target.value }))
                    }
                    placeholder="https://..."
                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleAddChapter}
                      disabled={saving}
                      className="rounded p-1 text-green-600 hover:bg-green-50"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAdding(false)
                        setError(null)
                      }}
                      className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {chapters.length === 0 && !isAdding && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-gray-400">
                  No chapters yet.
                </td>
              </tr>
            )}
            {chapters.map((ch) => (
              <tr key={ch.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">
                  {ch.chapter_no}
                </td>
                <td className="px-4 py-2 text-gray-600">{ch.title ?? '—'}</td>
                <td className="px-4 py-2 text-gray-600">{ch.sources.name}</td>
                <td className="max-w-xs px-4 py-2">
                  <a
                    href={ch.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-blue-600 hover:underline"
                  >
                    {ch.url}
                  </a>
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDelete(ch.id)}
                      className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
